import { Graph, GraphStoreType } from "./Graph"
import {
  useLazyExportGraphQuery,
  useLazyGetGraphDataQuery,
  useLazyGetUserGraphDataQuery,
} from "@store/services/graphsService"
import Box from "@mui/material/Box"
import CircularProgress from "@mui/material/CircularProgress"
import { GraphDto, UserGraphDto } from "@custom-types/graphs"
import {
  GraphDataViewModel,
  GraphEditMode,
  GraphNodeSelection,
  GraphSelectionModel,
} from "./graphViewModel"
import { FormControlLabel, Grid, Switch, Typography } from "@mui/material"
import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from "react"
import DiscardDialog from "@components/DiscardDialog"
import { GraphNodeData, GraphNodeEdgeData } from "@store/services/converter/types"
import {
  LOCAL_SCENARIO_HASH,
  deleteGraphFromIndexedDb,
  graphGetIndexedDb,
  graphGetIndexedDbData,
  graphSaveIndexedDb,
} from "@features/graph/state/indexedDBHelpers"
import { graphGroupExpandAll } from "@features/graph/handlers/graphGroupExpandAll"
import { Node, ReactFlowState, Viewport, useReactFlow, useStore, useStoreApi } from "reactflow"
import { QueryActionCreatorResult } from "@reduxjs/toolkit/dist/query/core/buildInitiate"
import { useAtom } from "jotai"
import {
  isModifiedAtom,
  useNodeSelection,
  useSetNodeSelection,
} from "@features/graph/state/graphAtoms"
import { graphUrlClearNodes, graphUrlRemoveNode } from "@features/graph/utils/graphUrl"
import { injectModifiedTkrisk } from "@features/graph/utils/injectModifiedTkrisk"
import { downloadFile } from "utils/file"
import NodeSelectionActions from "@features/graph/NodeSelectionActions"
import GraphActions from "@features/graph/GraphActions"
import GraphIndicators from "@features/graph/GraphIndicators"
import { useSearchParams } from "react-router"
import GraphCategories, { CategoriesTreeLevel } from "@features/graph/GraphCategories"
import { createCategoriesTree, findCategoryLevel } from "@features/graph/GraphCategories.utils"
import NodeCategoriesDrawer from "@features/graph/NodeCategoriesDrawer"
import { graphNodeClickHandler } from "@features/graph/handlers/graphNodeClick"
import { enqueueSnackbar } from "notistack"

interface GraphContainerProps {
  graph?: UserGraphDto | GraphDto
  graphData?: GraphNodeEdgeData
  viewPort?: Viewport
  onViewPortChange?: (viewPort: Viewport) => void
  isUserGraph: boolean
  setIsDrawerMultipleNodeVisible?: (bool: boolean) => void
  showAddToSelection?: boolean
  showModified?: boolean
  showUserGraphIndicator?: boolean
  showExportGraph?: boolean
  showNodeTypes?: boolean
  showResetGraph?: boolean
  editMode: GraphEditMode
  selectParentChild?: boolean
  graphFetchingErrorMessage?: string
  selectionChangeCallback?: (selection: GraphNodeSelection) => void
  onFetchingError?: () => void
  isMultiselectVisible?: boolean
  isMultipleRuns?: boolean
}

const nodesLengthSelector = (state: ReactFlowState) => state.getNodes().length

/**
 * Builds the zip handed to the user by the "Export graph" action.
 *
 * The zip from the backend only knows the server-side graph, so when the user has local
 * changes we add their tkrisk file to it as an extra entry. Untouched graphs are passed
 * through as-is, and a failed injection still downloads the backend zip.
 *
 * @param exportZip - zip body returned by `GET /graphs/{key}/export-graph`
 * @param graphKey - graph key used to look up the local copy in IndexedDB
 * @param fileName - name used for the injected entry when the zip holds no tkrisk file
 */
const buildExportZip = async (
  exportZip: ArrayBuffer,
  graphKey: string,
  fileName: string,
): Promise<Blob> => {
  try {
    const localGraph = await graphGetIndexedDb(graphKey)
    if (!localGraph?.modified) return new Blob([exportZip])

    return await injectModifiedTkrisk(exportZip, localGraph.base64, fileName)
  } catch {
    enqueueSnackbar("Could not add locally modified graph to the export", { variant: "warning" })
    return new Blob([exportZip])
  }
}

export const GraphContainer = ({
  graph,
  viewPort,
  onViewPortChange,
  isUserGraph,
  showAddToSelection = false,
  showUserGraphIndicator = false,
  showModified = true,
  showExportGraph = true,
  showNodeTypes = true,
  showResetGraph = true,
  selectionChangeCallback,
  setIsDrawerMultipleNodeVisible,
  editMode,
  selectParentChild,
  graphFetchingErrorMessage = "Graph can't be loaded",
  onFetchingError,
  isMultiselectVisible,
  isMultipleRuns,
  ...props
}: GraphContainerProps) => {
  const [fetchUserGraphData, { data: fetchedUserGraphData, isError: isErrorUserGraphError }] =
    useLazyGetUserGraphDataQuery()
  const [fetchBaseGraphData, { data: fetchedBaseGraphData, isError: isErrorBaseGraphError }] =
    useLazyGetGraphDataQuery()
  const [exportGraph] = useLazyExportGraphQuery()

  const [currentData, setCurrentData] = useState<GraphNodeEdgeData | undefined>(undefined)
  const [isDiscardOpen, setIsDiscardOpen] = useState(false)
  const [categoriesTree, setCategoriesTree] = useState<CategoriesTreeLevel[]>([])
  const [isRenderingGraph, setIsRenderingGraph] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
  const requestPromise = useRef<QueryActionCreatorResult<any> | null>(null)

  const [isModifiedFromAtom, setIsModified] = useAtom(isModifiedAtom)
  const isModified = isUserGraph ? true : isModifiedFromAtom
  const fetchedGraphData = fetchedUserGraphData ?? fetchedBaseGraphData
  const graphData = props.graphData ?? fetchedGraphData
  const isError = isErrorUserGraphError || isErrorBaseGraphError
  const nodeSelection = useNodeSelection()
  const { removeNode, clearNodes } = useSetNodeSelection()

  // rerender whenever number of nodes changes
  useStore(nodesLengthSelector)

  const graphStore: GraphStoreType = useStoreApi()
  const { fitView } = useReactFlow()

  const [searchParams, setSearchParams] = useSearchParams({})
  const isCategoriesViewEnabled = searchParams.get("isCategoriesViewEnabled") === "true"

  useEffect(() => {
    requestPromise.current = null
  }, [fetchedUserGraphData, fetchedBaseGraphData])

  async function onLoad() {
    if (!graph?.key || props.graphData) {
      return
    }

    let canLoad = !!(await graphGetIndexedDb(graph.key, graph.s3_hash))
    if (canLoad) {
      canLoad = await loadFromLocalStorage()
    }
    if (!canLoad) {
      loadFromRemote()
    }
  }

  useEffect(() => {
    if (requestPromise.current) {
      requestPromise.current.abort()
      requestPromise.current = null
    }
    setCurrentData(undefined)
    clearNodes()
    onLoad()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [graph])

  useEffect(() => {
    if (!graph?.key || !graphData) {
      return
    }
    graphSaveIndexedDb(
      graph.key,
      graphData,
      editMode === GraphEditMode.SCENARIO ? LOCAL_SCENARIO_HASH : graph.s3_hash,
    )
    setCurrentData(graphData)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [graphData])

  // Workaround for nesting components that are utilising the same query and it results with an error
  // https://github.com/reduxjs/redux-toolkit/issues/3942
  useEffect(() => {
    if (isError) {
      onFetchingError?.()
    }
  }, [onFetchingError, isError])

  const loadFromRemote = () => {
    if (!graph?.key) {
      return
    }

    requestPromise.current = isUserGraph
      ? fetchUserGraphData(graph.key)
      : fetchBaseGraphData(graph.key)
  }

  const loadFromLocalStorage = async (): Promise<boolean> => {
    if (!graph?.key) {
      return false
    }
    // try to load scenario
    let nodeEdgeData = await graphGetIndexedDbData(graph.key)
    // no scenario - try to load graph
    if (!nodeEdgeData) {
      nodeEdgeData = await graphGetIndexedDbData(graph.key, graph.s3_hash)
      // seed a fresh #scenario entry so subsequent edits (graphReplaceNode) can find it
      if (nodeEdgeData) {
        await graphSaveIndexedDb(graph.key, nodeEdgeData.data, LOCAL_SCENARIO_HASH, false)
      }
    }
    if (!nodeEdgeData) return false
    setIsModified(nodeEdgeData.modified)
    setCurrentData(nodeEdgeData.data)
    return true
  }

  const handleExport = async () => {
    if (!graph) {
      return
    }
    const resp = await exportGraph(graph.key)
    if (!resp.data) return enqueueSnackbar("Problem with exporting graph", { variant: "error" })
    const fileName = graph.base_graph_family_key.split("#").pop() ?? "graph"
    const blobUrl = URL.createObjectURL(await buildExportZip(resp.data, graph.key, fileName))
    downloadFile(`${fileName}.zip`, blobUrl)
  }

  const handleExpandAll = useCallback(() => {
    if (!currentData) {
      return
    }
    setIsRenderingGraph(true)
    graphGroupExpandAll(currentData, graphStore)
    setTimeout(() => {
      fitView({ duration: 0, maxZoom: 1 })
      setIsRenderingGraph(false)
    })
  }, [currentData, fitView, graphStore])

  const handleClearSelection = () => {
    clearNodes()
    graphUrlClearNodes()
  }

  const handleDiscard = useCallback(async () => {
    GraphDataViewModel.clear()
    setIsModified(false)
    setCurrentData(undefined)
    // remove #scenario
    if (graph?.key) deleteGraphFromIndexedDb(graph.key)
    // load #s3hash file
    await loadFromLocalStorage()
    setIsDiscardOpen(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [])

  GraphDataViewModel.onLoad(
    useCallback(() => {
      const tree = createCategoriesTree(GraphDataViewModel.data?.groupMap ?? {})
      setCategoriesTree(tree)
    }, []),
  )
  const currentLevelId = searchParams.get("categoryId") ?? "root"
  const currentLevel = useMemo(
    () => findCategoryLevel(categoriesTree, currentLevelId),
    [categoriesTree, currentLevelId],
  )
  const isLowestCategoryLevel = currentLevel?.groups.length === 0

  useEffect(() => {
    // Keep this recovery path when categories view is off.
    // After graph updates while React Flow is hidden (display: none), selection can point
    // to a missing node and viewport state may be stale. We only skip expand-all when the
    // currently selected node still exists in store.
    if (!isCategoriesViewEnabled) {
      const allNodes = graphStore.getState().getNodes()
      const selectedNode = allNodes.find(({ id }) => id === GraphSelectionModel.selected)
      if (selectedNode) return
    }

    // To properely navigate through groups in category view, we need to have everything expanded under the hood.
    // It comms down to the fact that drawer is tied up directly to reactflow graph store.
    handleExpandAll()
  }, [fitView, graphStore, handleExpandAll, isCategoriesViewEnabled])

  if (!graph || !currentData) {
    return <GraphLoader />
  }
  if (isError) {
    return <GraphLoadError message={graphFetchingErrorMessage} />
  }

  const handleDeleteNodeFromSelection = (node: Node) => {
    removeNode(node.id)
    graphUrlRemoveNode(node.id)
  }

  const handleCategoriesDrawerNodeClick = (node: GraphNodeData) => {
    if (!selectionChangeCallback) return

    const allNodes = graphStore.getState().getNodes()
    const nodeToSelect = allNodes.find(({ id }) => id === node.node.name)
    if (!nodeToSelect) return

    graphNodeClickHandler(nodeToSelect.id, graphStore)
  }

  const handleCategoryViewToggle = (_e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setSearchParams((params) => {
      params.set("isCategoriesViewEnabled", checked.toString())
      return params
    })

    // on turn off, make sure graph is focused on node that has its drawer opened
    if (!checked && GraphSelectionModel.selected) {
      setTimeout(() => {
        const allNodes = graphStore.getState().getNodes()
        const selectedNode = allNodes.find(({ id }) => id === GraphSelectionModel.selected)
        if (!selectedNode) return

        fitView({ nodes: [selectedNode], duration: 0, maxZoom: 2.5 })
      })
    }
  }

  return (
    <Grid container direction="column" sx={{ height: "100%" }} wrap="nowrap">
      <Grid item>
        <FormControlLabel
          label="Categories view"
          control={<Switch checked={isCategoriesViewEnabled} onChange={handleCategoryViewToggle} />}
          data-testid="categories-view-toggle"
        />
      </Grid>

      <Grid item>
        {isMultiselectVisible && Object.values(nodeSelection).length > 0 && (
          <NodeSelectionActions
            selectedNodes={Object.values(nodeSelection)}
            onClearSelectionClick={handleClearSelection}
            onDeleteNodeClick={handleDeleteNodeFromSelection}
            onCompareNodesClick={
              setIsDrawerMultipleNodeVisible
                ? () => setIsDrawerMultipleNodeVisible(true)
                : undefined
            }
          />
        )}
        <GraphActions
          showExpandAllButton={Object.keys(currentData.groupMap).length > 0}
          showAddToSelection={showAddToSelection}
          showResetGraphButton={showResetGraph}
          showExportGraphButton={showExportGraph}
          showNodeTypesButton={showNodeTypes}
          onExpandAllClick={handleExpandAll}
          onResetGraphClick={() => setIsDiscardOpen(true)}
          onExportGraphClick={handleExport}
        >
          <GraphIndicators
            showModifiedIndicator={showModified}
            showUserGraphIndicator={showUserGraphIndicator && isUserGraph}
            isModified={isModified}
          />
        </GraphActions>
      </Grid>

      {isCategoriesViewEnabled && (
        <Grid item xs sx={{ minHeight: 0, overflow: "hidden" }}>
          <Grid
            container
            direction={isLowestCategoryLevel ? "column" : "row"}
            sx={{
              height: "100%",
              minHeight: 0,
              overflow: isCategoriesViewEnabled ? "hidden" : undefined,
            }}
            style={{ minWidth: "30%" }}
            wrap="nowrap"
            data-testid="category-view-container"
          >
            <Grid
              item
              xs={isLowestCategoryLevel ? "auto" : true}
              sx={isLowestCategoryLevel ? { width: "100%" } : undefined}
            >
              <GraphCategories tree={categoriesTree} compact={isLowestCategoryLevel} />
            </Grid>
            <Grid
              item
              xs={isLowestCategoryLevel ? true : "auto"}
              sx={{ display: "flex", ...(isLowestCategoryLevel && { width: "100%" }) }}
            >
              <NodeCategoriesDrawer
                groupId={currentLevelId}
                onNodeListItemClick={handleCategoriesDrawerNodeClick}
                fillAvailableSpace={isLowestCategoryLevel}
              />
            </Grid>
          </Grid>
        </Grid>
      )}

      <Grid item xs sx={{ display: isCategoriesViewEnabled ? "none" : undefined, minHeight: 0 }}>
        <Graph
          graph={graph}
          viewPort={viewPort}
          onViewPortChange={onViewPortChange}
          graphData={currentData}
          selectionChangeCallback={selectionChangeCallback}
          editMode={editMode}
          selectParentChild={selectParentChild}
          skipChipsSelection={isMultipleRuns}
          style={{ display: isCategoriesViewEnabled ? "none" : "block" }}
        />
      </Grid>

      <DiscardDialog
        open={isDiscardOpen}
        setOpen={setIsDiscardOpen}
        onAcceptClick={handleDiscard}
      />
    </Grid>
  )
}

const GraphLoader: FunctionComponent = () => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "calc(100% - 150px)",
      my: 6,
    }}
  >
    <CircularProgress />
  </Box>
)

const GraphLoadError: FunctionComponent<{ message?: string }> = ({ message }) => (
  <Grid container alignItems="center" justifyContent="center" sx={{ height: "100%" }}>
    <Grid item>
      <Typography>
        ⚠️ <strong>{message}</strong>
      </Typography>
    </Grid>
  </Grid>
)
